import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminResources() {
  const [resources, setResources] = useState([]);
  const [form, setForm] = useState({ title: "", url: "", description: "" });

  const load = () => api.get("/resources").then(({ data }) => setResources(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title || !form.url) return toast.error("Title and URL required");
    await api.post("/resources", form);
    setForm({ title: "", url: "", description: "" });
    load();
    toast.success("Resource added");
  };

  const remove = async (id) => {
    await api.delete(`/resources/${id}`);
    load();
    toast.success("Removed");
  };

  return (
    <div className="space-y-6" data-testid="admin-resources">
      <div>
        <p className="text-label">Content</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">Resource Hub Management</h1>
        <p className="text-sm text-muted-foreground">Links and downloads shown to providers.</p>
      </div>

      <Card className="p-5 space-y-3">
        <h3 className="font-display font-semibold">Add Resource</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-label">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="res-title" />
          </div>
          <div>
            <Label className="text-label">URL</Label>
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." className="mt-1" data-testid="res-url" />
          </div>
          <div>
            <Label className="text-label">Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="res-desc" />
          </div>
        </div>
        <Button onClick={add} data-testid="add-resource"><Plus className="w-4 h-4 mr-2" /> Add</Button>
      </Card>

      <Card className="p-5">
        <h3 className="font-display font-semibold mb-3">Existing Resources</h3>
        <div className="space-y-2">
          {resources.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-3 border border-border rounded-md">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{r.title}</p>
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline truncate flex items-center gap-1">
                  {r.url} <ExternalLink className="w-3 h-3" />
                </a>
                {r.description && <p className="text-xs text-muted-foreground">{r.description}</p>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(r.id)} data-testid={`remove-${r.id}`}>
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
